import React, { useEffect, useMemo, useState } from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import { LibraryFacilityType, facilityLabel, filterFacilityTypes } from "@data/regulationLibraryCatalog";
import { CoverageArea, getRegulationLibrary } from "@services/regulationLibrary";
import searchIcon from "@assets/regulation-library/search.svg";
import checkIcon from "@assets/regulation-library/check.svg";
import federalIcon from "@assets/regulation-library/federal.svg";
import cfrIcon from "@assets/regulation-library/cfr.svg";
import stateIcon from "@assets/regulation-library/state.svg";
import opsIcon from "@assets/regulation-library/ops.svg";

const ICONS = {
  federal: federalIcon,
  cfr: cfrIcon,
  state: stateIcon,
  ops: opsIcon,
};

function Highlight({ text, query }: { text: string; query: string }) {
  const needle = query.trim();
  if (!needle) return <>{text}</>;
  const lower = text.toLowerCase();
  const q = needle.toLowerCase();
  const nodes: React.ReactNode[] = [];
  let cursor = 0;
  let index = lower.indexOf(q, cursor);
  let key = 0;
  while (index >= 0) {
    if (index > cursor) nodes.push(text.slice(cursor, index));
    nodes.push(
      <Box key={key} component="mark" sx={{ backgroundColor: "#d7f6fb", color: "inherit", padding: 0 }}>
        {text.slice(index, index + needle.length)}
      </Box>
    );
    key += 1;
    cursor = index + needle.length;
    index = lower.indexOf(q, cursor);
  }
  if (cursor < text.length) nodes.push(text.slice(cursor));
  return <>{nodes}</>;
}

interface RegulationLibraryLandingProps {
  onSelectFacility: (facility: LibraryFacilityType) => void;
  onBrowseFacilityTypes: () => void;
}

export default function RegulationLibraryLanding({
  onSelectFacility,
  onBrowseFacilityTypes,
}: RegulationLibraryLandingProps) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [facilityTypes, setFacilityTypes] = useState<LibraryFacilityType[]>([]);
  const [quickPickIds, setQuickPickIds] = useState<string[]>([]);
  const [coverageAreas, setCoverageAreas] = useState<CoverageArea[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const matches = useMemo(() => filterFacilityTypes(facilityTypes, query), [facilityTypes, query]);

  useEffect(() => {
    let cancelled = false;
    getRegulationLibrary()
      .then((payload) => {
        if (cancelled) return;
        setFacilityTypes(payload.facilityTypes);
        setQuickPickIds(payload.quickPickIds);
        setCoverageAreas(payload.coverageAreas);
        setError(null);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : "Failed to load the regulations library");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const choose = (facility: LibraryFacilityType) => {
    setQuery("");
    setOpen(false);
    onSelectFacility(facility);
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "28px", width: "100%", pb: 3 }}>
      <Typography sx={{ fontFamily: "Rethink Sans, sans-serif", fontSize: 14, color: "#4f4f4f", lineHeight: 1.5 }}>
        Federal, state, and accreditation compliance requirements for all available healthcare facility types — scoped to your state.
      </Typography>
      {loading && <CircularProgress size={28} sx={{ color: "#0a829d" }} aria-label="Loading facility types" />}
      {error && (
        <Typography sx={{ color: "#8a1f11", fontFamily: "Rethink Sans, sans-serif", fontSize: 14 }}>{error}</Typography>
      )}

      <Box
        sx={{
          border: "1px solid #c5eef5",
          backgroundColor: "#f3fdff",
          borderRadius: "12px",
          padding: "22px 28px 20px",
          width: "100%",
          maxWidth: 880,
          boxSizing: "border-box",
        }}
      >
        <Typography sx={{ fontFamily: "Rethink Sans, sans-serif", fontSize: 13, fontWeight: 600, color: "#065b70", mb: 1.5 }}>
          Select Your Facility Type
        </Typography>
        <Box sx={{ position: "relative", width: "100%" }}>
          <Box
            component="img"
            src={searchIcon}
            alt=""
            width={18}
            height={18}
            sx={{ position: "absolute", left: 16, top: 16 }}
          />
          <Box
            component="input"
            aria-label="Search by facility type"
            aria-expanded={open}
            aria-controls="regulation-library-facility-list"
            value={query}
            placeholder="Search by facility type..."
            onChange={(event) => {
              setQuery(event.target.value);
              setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            sx={{
              width: "100%",
              boxSizing: "border-box",
              height: 50,
              borderRadius: "8px",
              border: "1px solid #9ad7e4",
              backgroundColor: "#fff",
              padding: "0 16px 0 44px",
              fontFamily: "Rethink Sans, sans-serif",
              fontSize: 14,
              color: "#262626",
              outline: "none",
            }}
          />
          {open && (
            <Box
              id="regulation-library-facility-list"
              role="listbox"
              sx={{
                position: "absolute",
                zIndex: 2,
                top: 54,
                left: 0,
                right: 0,
                maxHeight: 280,
                overflowY: "auto",
                backgroundColor: "#fff",
                border: "1px solid #c5eef5",
                borderRadius: "8px",
                boxShadow: "0 8px 20px rgba(10, 130, 157, 0.12)",
              }}
            >
              {matches.length === 0 ? (
                <Typography sx={{ px: 2, py: 1.5, fontSize: 14, color: "#737373" }}>No facility types match.</Typography>
              ) : (
                matches.map((facility) => (
                  <Box
                    key={facility.id}
                    role="option"
                    component="button"
                    type="button"
                    onClick={() => choose(facility)}
                    sx={{
                      display: "block",
                      width: "100%",
                      textAlign: "left",
                      border: 0,
                      background: "transparent",
                      cursor: "pointer",
                      px: 2,
                      py: 1.25,
                      fontFamily: "Rethink Sans, sans-serif",
                      fontSize: 14,
                      color: "#065b70",
                      "&:hover": { backgroundColor: "#f3fdff" },
                    }}
                  >
                    <Highlight text={facilityLabel(facility)} query={query} />
                  </Box>
                ))
              )}
            </Box>
          )}
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1.5, flexWrap: "wrap" }}>
          <Typography sx={{ fontSize: 13, color: "#737373", fontFamily: "Rethink Sans, sans-serif" }}>Common:</Typography>
          {quickPickIds.map((id) => {
            const facility = facilityTypes.find((entry) => entry.id === id);
            if (!facility) return null;
            return (
              <Box
                key={id}
                component="button"
                type="button"
                onClick={() => choose(facility)}
                sx={{
                  border: "1px solid #bfe5ec",
                  backgroundColor: "#fff",
                  color: "#0c6478",
                  borderRadius: "16px",
                  px: 1.25,
                  py: "5px",
                  fontSize: 12,
                  fontWeight: 500,
                  fontFamily: "Rethink Sans, sans-serif",
                  cursor: "pointer",
                }}
              >
                {facility.acronym ?? facility.name}
              </Box>
            );
          })}
        </Box>
      </Box>

      <Box>
        <Typography sx={{ fontFamily: "Rethink Sans, sans-serif", fontSize: 18, fontWeight: 600, color: "#0b3c49", mb: 0.5 }}>
          What&apos;s inside
        </Typography>
        <Typography sx={{ fontFamily: "Rethink Sans, sans-serif", fontSize: 14, color: "#5c6b73", mb: 2 }}>
          Comprehensive compliance coverage across all regulatory areas
        </Typography>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2 }}>
          {coverageAreas.map((area) => (
            <Box
              key={area.id}
              sx={{
                border: "1px solid #d7f1f6",
                borderRadius: "12px",
                backgroundColor: "#fff",
                p: 2,
                minHeight: 136,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: "10px",
                    backgroundColor: "#e7f8fc",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Box component="img" src={ICONS[area.icon]} alt="" width={20} height={20} />
                </Box>
                <Typography sx={{ fontFamily: "Rethink Sans, sans-serif", fontWeight: 600, fontSize: 16, color: "#065b70" }}>
                  {area.title}
                </Typography>
              </Box>
              <Box sx={{ display: "grid", gridTemplateColumns: area.items.length > 3 ? "1fr 1fr" : "1fr", columnGap: 2, rowGap: 0.5 }}>
                {area.items.map((line) => (
                  <Box key={line} sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                    <Box component="img" src={checkIcon} alt="" width={14} height={14} sx={{ mt: "3px" }} />
                    <Typography sx={{ fontFamily: "Rethink Sans, sans-serif", fontSize: 13, color: "#3d4a50" }}>{line}</Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          ))}
        </Box>
      </Box>

      <Box
        component="button"
        type="button"
        onClick={onBrowseFacilityTypes}
        sx={{
          display: "none",
          alignSelf: "flex-start",
          border: 0,
          background: "transparent",
          color: "#0a829d",
          fontFamily: "Rethink Sans, sans-serif",
          fontSize: 13,
          fontWeight: 600,
          cursor: "pointer",
          p: 0,
        }}
      >
        Set up a facility
      </Box>
    </Box>
  );
}
